import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import Link from 'next/link';
import Image from 'next/legacy/image';

import { ROOT_PATH } from './[slug]';
import { getPosts } from '../../client/ghost';
import { PostOrPage } from '@tryghost/content-api';
import { formatDate } from '../../client/components/blog/helpers';

export async function getStaticProps() {
  const posts = await getPosts({ filter: 'tag:Blog' });

  if (!posts) {
    return {
      notFound: true,
    };
  }

  return {
    props: { posts },
  };
}

const BlogIndexPage = ({ posts }: { posts: PostOrPage[] }) => {
  return (
    <>
      <NextSeo
        title={`Stackblocks Blog`}
        description="We talk about how to embed your content, newsletters, podcasts, videos, and more on websites built on different platforms."
        canonical={`https://stackblocks.app${ROOT_PATH}`}
        openGraph={{
          url: `https://stackblocks.app${ROOT_PATH}`,
          title: `Stackblocks Blog`,
          description:
            'We talk about how to embed your content, newsletters, podcasts, videos, and more on websites built on different platforms.',
          images: [
            {
              url: `https://stackblocks.app/stackblocks-opengraph.png`,
              width: 1200,
              height: 627,
              alt: 'Stackblocks screenshot',
            },
          ],
          site_name: 'Stackblocks Webiste',
        }}
        twitter={{
          handle: '@aravindballa',
          site: '@StackblocksApp',
          cardType: 'summary_large_image',
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          {
            position: 1,
            name: 'Stackblocks',
            item: `https://stackblocks.app`,
          },
          {
            position: 2,
            name: 'Guides',
            item: `https://stackblocks.app${ROOT_PATH}`,
          },
        ]}
      />
      <div className="w-full max-w-screen-xl h-full flex-1 mx-auto my-12 px-8">
        <h1 className="font-bold text-5xl mb-24 font-serif">Blog</h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post) => (
            <div key={post.slug} className="w-full">
              <Link href={`${ROOT_PATH}/${post.slug}`} className="flex flex-col items-start group">
                {post.feature_image && (
                  <div className="relative w-full h-auto aspect-[2/1] overflow-hidden rounded-lg shadow-img-shadow group-hover:scale-105 transition-transform">
                    <Image
                      src={post.feature_image}
                      alt={post.feature_image_alt || post.title || 'Blog post feature image'}
                      layout="fill"
                      className="rounded-lg"
                    />
                  </div>
                )}
                <h2 className="text-2xl font-bold my-4 font-serif">{post.title}</h2>
                <p className="text-gray-600 text-sm mb-4">
                  Updated on {formatDate(post.updated_at || post.published_at || 0)}
                </p>
                <p className="">{(post.excerpt || post.meta_description)?.slice(0, 200)}...</p>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default BlogIndexPage;
